import { useQuery } from "@tanstack/react-query"
import {
  routes,
  type SearchRankingKeywordDetail,
  type SearchRankingMarketplaceList,
  type SearchRankingOverview,
  type SearchRankingTitleOpportunities,
  type SearchProductRankings,
} from "@nova-ai/shared"
import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

/** Which search marketplaces the API actually has configured. The dashboard
 * discovers them instead of repeating an id at every link to the tracker. */
export function useSearchMarketplaces() {
  return useQuery({
    queryKey: queryKeys.searchMarketplaces,
    queryFn: async () => {
      const result = await apiFetch<SearchRankingMarketplaceList>(routes.searchMarketplaces.list())
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value.marketplaces
    },
  })
}

export function useSearchRankingOpportunities(
  marketplace: string,
  phrase: string,
  range: { from: string; to: string } | null
) {
  return useQuery({
    queryKey: queryKeys.searchRankingOpportunities(
      marketplace,
      phrase,
      range?.from ?? "",
      range?.to ?? ""
    ),
    enabled: range !== null,
    queryFn: async () => {
      const path = routes.searchRankings.keywordOpportunities({ marketplace, phrase })
      const params = new URLSearchParams({ from: range?.from ?? "", to: range?.to ?? "" })
      const result = await apiFetch<SearchRankingTitleOpportunities>(`${path}?${params}`)
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

export function useSearchRankingOverview(marketplace: string) {
  return useQuery({
    queryKey: queryKeys.searchRankingOverview(marketplace),
    queryFn: async () => {
      const result = await apiFetch<SearchRankingOverview>(
        routes.searchRankings.overview({ marketplace })
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

export function useSearchProductRankings(marketplace: string, sku: string) {
  return useQuery({
    queryKey: queryKeys.searchProductRankings(marketplace, sku),
    queryFn: async () => {
      const result = await apiFetch<SearchProductRankings>(
        routes.searchRankings.productRankings({ marketplace, sku })
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

/** `runId` pins the detail to one capture's snapshot; null follows the latest.
 *  It is part of the query key so stepping between captures is cached per
 *  capture instead of refetching the one you just came back to. */
export function useSearchRankingKeyword(
  marketplace: string,
  phrase: string | null,
  runId: string | null = null
) {
  return useQuery({
    queryKey: queryKeys.searchRankingKeyword(marketplace, phrase ?? "", runId ?? ""),
    enabled: phrase !== null,
    queryFn: async () => {
      const path = routes.searchRankings.keyword({ marketplace, phrase: phrase ?? "" })
      const result = await apiFetch<SearchRankingKeywordDetail>(
        runId === null ? path : `${path}?runId=${encodeURIComponent(runId)}`
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}
